import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Microphone, Stop, WaveformSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VoiceSearchButton = ({ onResult, className = '', size = 'md' }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const supported = useRef(typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window));

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  useEffect(() => { return () => stop(); }, [stop]);

  const start = () => {
    if (!supported.current) {
      toast.error('Voice search is not supported in your browser. Try Chrome or Edge.');
      return;
    }
    if (listening) { stop(); return; }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript && onResult) {
        onResult(transcript.trim());
        toast.success(`Searching: "${transcript.trim()}"`);
      }
      setListening(false);
    };
    recognition.onerror = (event) => {
      if (event.error === 'no-speech') toast.info('No speech detected. Try again.');
      else if (event.error === 'not-allowed') toast.error('Microphone access denied. Please allow microphone in browser settings.');
      else if (event.error !== 'aborted') toast.error('Voice search error. Try again.');
      setListening(false);
    };
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  const sizeClasses = size === 'sm'
    ? 'w-8 h-8'
    : size === 'lg'
    ? 'w-12 h-12'
    : 'w-10 h-10';

  const iconSize = size === 'sm' ? 14 : size === 'lg' ? 22 : 18;

  return (
    <button
      type="button"
      onClick={start}
      className={`${sizeClasses} rounded-full flex items-center justify-center transition-all shrink-0 ${
        listening
          ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
          : 'bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-600'
      } ${className}`}
      title={listening ? 'Stop listening' : 'Voice search'}
      data-testid="voice-search-btn"
    >
      {listening ? <Stop size={iconSize} weight="fill" /> : <Microphone size={iconSize} weight="bold" />}
    </button>
  );
};

export default VoiceSearchButton;
